import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import { ELEVENLABS_CONFIG } from '../config/intakeConfig';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';

/**
 * ElevenLabsIntakePopover
 * Centered modal where the ElevenLabs widget fully occupies the popover.
 * - The iframe fills 100% of the popover. No inner header/footer steals space.
 * - Top-right overlay action buttons:
 *   - I'm Done (primary): returns transcript as first message
 *   - Cancel (text): closes the popover
 * - Inside the iframe:
 *   - elevenlabs-convai fills 100% (variant=expanded)
 *   - Script forwards user/agent events via postMessage for transcript aggregation
 *   - Attempts to remove/hide “Powered by ElevenLabs Conversational AI” branding node
 *     and keep the widget maximized within the popover
 */
export default function ElevenLabsIntakePopover({
  open,
  onClose,
  onConfirm,
  agentId: agentIdProp,
  width = 1100,
  height = 820
}) {
  const agentId = useMemo(
    () =>
      agentIdProp ||
      ELEVENLABS_CONFIG?.AGENT_ID ||
      (typeof window !== 'undefined' && window.ELEVENLABS_AGENT_ID) ||
      'MxvnsnriCpWRZSm1Pnou',
    [agentIdProp]
  );

  const [turns, setTurns] = useState([]); // [{ role: 'user'|'agent', text, ts }]
  const iframeRef = useRef(null);
  const [autoSize, setAutoSize] = useState(null);

  // Build iframe srcdoc with widget tag + script; widget fills the iframe 100%
  const iframeSrcDoc = useMemo(() => {
    const safeAgentId = String(agentId).replace(/"/g, '"');
    return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1,viewport-fit=cover" />
    <script src="https://unpkg.com/@elevenlabs/convai-widget-embed" async type="text/javascript"></script>
    <style>
      html, body {
        margin: 0;
        padding: 0;
        height: 100%;
        width: 100%;
        background: transparent;
        display: flex;
        align-items: stretch;
        justify-content: stretch;
      }
      .wrap {
        position: relative;
        height: 100%;
        width: 100%;
        background: transparent;
        display: flex;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden !important;
      }
      elevenlabs-convai {
        display: block;
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        contain: layout size style;
        max-width: 100% !important;
        max-height: 100% !important;
        object-fit: fill;
      }
      .wrap > * {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        max-width: 100% !important;
        max-height: 100% !important;
      }
      /* Soft cover strip to hide any vendor footer/branding without blocking input */
      #branding-cover {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 0px;
        background: transparent;
        z-index: 1;
        pointer-events: none;
      }
      /* Keep branding visible (reverted) */
      a[href*="elevenlabs"], footer a[href*="elevenlabs"], footer {
        display: initial !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
    </style>
  </head>
  <body>
    <div class="wrap">
      <elevenlabs-convai
        agent-id="${safeAgentId}"
        variant="expanded"
        style="width:100%;height:100%;"
      ></elevenlabs-convai>
      <div id="branding-cover" aria-hidden="true"></div>
    </div>
    <script>
      // Forward common widget events up to the parent window
      (function () {
        function fwd(evtName, e) {
          try {
            var detail = (e && e.detail) || {};
            var payload = {
              source: 'elevenlabs_iframe',
              type: detail.type || evtName,
              data: detail.data || detail
            };
            parent.postMessage(payload, '*');
          } catch (_) {}
        }
        function bind(name) {
          try {
            window.addEventListener(name, function (e) { fwd(name, e); });
            document.addEventListener(name, function (e) { fwd(name, e); });
          } catch (_) {}
        }
        [
          // Core aggregate event + explicit aliases
          'elevenlabs-widget',
          'elevenlabs-conversation-started',
          'conversation_started',
          'elevenlabs-conversation-ended',
          'conversation_ended',
          // User-side events
          'elevenlabs-user-message',
          'user_message',
          'elevenlabs-user-transcript',
          'user_transcript',
          // Agent-side events
          'elevenlabs-agent-message',
          'agent_message',
          'elevenlabs-agent-response',
          'agent_response'
        ].forEach(bind);
      })();

      // Also forward any window.postMessage payloads up to the parent in case the widget uses message-based transport only
      (function () {
        function forwardAnyWindowMessage(ev) {
          try {
            parent.postMessage(
              {
                source: 'elevenlabs_iframe',
                type: 'postmessage',
                data: ev && ev.data
              },
              '*'
            );
          } catch (_) {}
        }
        try {
          window.addEventListener('message', forwardAnyWindowMessage, false);
        } catch (_) {}
      })();

      // Best-effort: remove/hide "Powered by ElevenLabs Conversational AI" and maximize layout
      (function () {
        function tryRemoveBranding(root) {
          try {
            // Remove any anchors that include elevenlabs.io with 'Conversational AI' nearby
            var anchors = root.querySelectorAll('a[href*="elevenlabs"]');
            anchors.forEach(function(a) {
              var text = (a.textContent || '').toLowerCase();
              if (text.includes('conversational ai') || text.includes('elevenlabs')) {
                // Hide the anchor and its immediate container if it looks like a footer
                a.style.display = 'none';
                if (a.parentElement) a.parentElement.style.display = 'none';
              }
            });

            // Also scan for plain text nodes containing 'Powered by', hide their container
            var all = root.querySelectorAll('div,span,footer,p,section');
            all.forEach(function(n) {
              var t = (n.textContent || '').trim().toLowerCase();
              if (t.includes('powered by') && t.includes('elevenlabs')) {
                n.style.display = 'none';
              }
            });
          } catch (_) {}
        }

        function hideBrandingByText(root) {
          try {
            if (!root || !root.querySelectorAll) return;
            var nodes = root.querySelectorAll('*');
            nodes.forEach(function(el){
              try {
                var txt = (el.textContent || '').toLowerCase();
                if (txt && txt.includes('powered by') && txt.includes('elevenlabs')) {
                  el.style.display = 'none';
                  if (el.parentElement) el.parentElement.style.display = 'none';
                }
              } catch (_) {}
            });
          } catch (_) {}
        }

        function maximizeShadow(el) {
          try {
            var sr = el && el.shadowRoot;
            if (!sr) return false;

            // 1) Ensure host fills the popover
            var host = el;
            host.style.position = 'absolute';
            host.style.inset = '0';
            host.style.width = '100%';
            host.style.height = '100%';

            // 2) Inject a style tag into shadow root to force full-bleed and squash inner spacing
            try {
              var injected = sr.getElementById && sr.getElementById('__imax');
              if (!injected) {
                var st = document.createElement('style');
                st.id = '__imax';
                st.textContent = ':host { position: absolute !important; inset: 0 !important; width: 100% !important; height: 100% !important; }\n* , *::before, *::after {\n  max-width: none !important;\n  border-radius: 0 !important;\n  box-shadow: none !important;\n  margin: 0 !important;\n  padding: 0 !important;\n}\n.container, .wrapper, .content, .root, .panel, .shell, .layout, .viewport {\n  width: 100% !important;\n  height: 100% !important;\n}';
                sr.appendChild(st);
              }
            } catch (_) {}

            // 3) Stretch likely root container
            var rootDiv = sr.querySelector('div') || sr.firstElementChild;
            if (rootDiv) {
              rootDiv.style.width = '100%';
              rootDiv.style.height = '100%';
              rootDiv.style.maxWidth = 'none';
              rootDiv.style.maxHeight = 'none';
            }

            // 4) Aggressively remove constraints and reposition sticky/fixed items
            Array.from(sr.querySelectorAll('*')).forEach(function(n){
              try {
                var cs = getComputedStyle(n);
                if (n.style && n.style.maxWidth) n.style.maxWidth = 'none';
                var cls = (n.className || '').toString();
                if (/container|wrapper|content|root|panel|shell|layout|viewport/i.test(cls)) {
                  n.style.width = '100%';
                  if (!n.style.height || /auto/.test(n.style.height)) {
                    n.style.height = '100%';
                  }
                }
                n.style.padding = '0';
                n.style.margin = '0';
                n.style.borderRadius = '0';
                n.style.boxShadow = 'none';

                // Force fixed/sticky panels to fill the popover
                if (cs && (cs.position === 'fixed' || cs.position === 'sticky')) {
                  n.style.position = 'absolute';
                  n.style.inset = '0';
                  n.style.width = '100%';
                  n.style.height = '100%';
                  n.style.maxWidth = 'none';
                  n.style.maxHeight = 'none';
                }
                // Remove transforms pushing the card to a corner
                if (cs && cs.transform && cs.transform !== 'none') {
                  n.style.transform = 'none';
                  n.style.translate = 'none';
                  n.style.transformOrigin = 'center center';
                }
                // If anchored bottom-right, re-anchor to fill
                if (cs && (cs.right === '0px' || cs.bottom === '0px')) {
                  n.style.left = '0';
                  n.style.top = '0';
                  n.style.right = 'auto';
                  n.style.bottom = 'auto';
                  n.style.inset = '0';
                  n.style.width = '100%';
                  n.style.height = '100%';
                }
              } catch (_) {}
            });

            // 5) Within shadow root, scale up the largest "panel" if still undersized
            try {
              // Prefer sizing relative to the full iframe body area, not just the host
              var __wrap = document.querySelector('.wrap') || document.body || document.documentElement;
              var __rect = __wrap.getBoundingClientRect();
              var scw = __rect.width || (sr.host && sr.host.clientWidth) || 0;
              var sch = __rect.height || (sr.host && sr.host.clientHeight) || 0;

              var nodes = Array.from(sr.querySelectorAll('*'));
              var best = null;
              var bestArea = 0;

              nodes.forEach(function(n){
                try {
                  var cs = getComputedStyle(n);
                  var r = n.getBoundingClientRect();
                  var looksLikePanel =
                    cs &&
                    ((cs.boxShadow && cs.boxShadow !== 'none') || parseFloat(cs.borderRadius || '0') > 8);
                  var hasControls = n.querySelector && n.querySelector('input, textarea, button, [contenteditable], form');
                  var area = (r.width || 0) * (r.height || 0);
                  if (looksLikePanel && hasControls && area > bestArea) {
                    best = n;
                    bestArea = area;
                  }
                } catch (_) {}
              });

              if (best && scw && sch) {
                // Stretch it first
                best.style.position = 'absolute';
                best.style.inset = '0';
                best.style.left = '0';
                best.style.top = '0';
                best.style.width = '100%';
                best.style.height = '100%';
                best.style.margin = '0';
                best.style.borderRadius = '0';
                best.style.boxShadow = 'none';
                best.style.maxWidth = 'none';
                best.style.maxHeight = 'none';
                best.style.padding = '0';
                best.style.transform = 'none';

                var br = best.getBoundingClientRect();
                // Fill almost entirely: use a very small gutter (0.1%)
                var sX = (scw * 0.999) / Math.max(br.width || 1, 1);
                var sY = (sch * 0.999) / Math.max(br.height || 1, 1);
                var s = Math.min(sX, sY);
                // Aggressive over-scale (12%) but cap total scale conservatively
                var s2 = (s && s > 1.001) ? Math.min(1.35, s * 1.12) : 1;
                if (s2 && s2 > 1.001) {
                  // Scale from center so expansion grows uniformly
                  best.style.transformOrigin = 'center center';
                  best.style.transform = 'scale(' + s2 + ')';
                  best.style.willChange = 'transform';
                }

                // Fallback: if the card still doesn't visually fill width/height after the first pass,
                // apply a secondary scale on the shadow host itself to push nested layouts wider.
                setTimeout(function () {
                  try {
                    var br2 = best.getBoundingClientRect();
                    var needsWider = br2.width < scw * 0.995;
                    var needsTaller = br2.height < sch * 0.995;
                    if (needsWider || needsTaller) {
                      var host = sr && sr.host ? sr.host : el;
                      if (host && host.style) {
                        // Compute additional scale needed on host
                        var sHX = (scw * 0.999) / Math.max(br2.width || 1, 1);
                        var sHY = (sch * 0.999) / Math.max(br2.height || 1, 1);
                        var sH = Math.min(1.5, Math.min(sHX, sHY));
                        if (sH > 1.001) {
                          host.style.transformOrigin = 'center center';
                          host.style.transform = 'scale(' + sH + ')';
                          host.style.willChange = 'transform';
                        }
                      }
                    }
                  } catch (_){}
                }, 60);
              }
            } catch (_) {}

            // Keep branding visible
            return true;
          } catch (_) { return false; }
        }

        function enhance() {
          // Ensure page has no scroll/padding that could create whitespace
          try {
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
            document.body.style.padding = '0';
            document.body.style.margin = '0';
          } catch (_) {}

          var el = document.querySelector('elevenlabs-convai');
          if (!el) return false;

          // Keep branding visible

          // Try shadow manipulations to stretch host/root
          var ok = maximizeShadow(el);

          // Attempt to 'open' or expand the chat if a button exists
          try {
            var sr = el.shadowRoot;
            if (sr) {
              var openBtn =
                sr.querySelector('button[aria-label*="Open" i]') ||
                sr.querySelector('button[aria-label*="Start" i]') ||
                sr.querySelector('button[aria-label*="Chat" i]') ||
                sr.querySelector('button');
              if (openBtn) {
                try {
                  openBtn.click();
                  // Try again shortly in case the first click only reveals a first-stage UI
                  setTimeout(function(){ try { openBtn.click(); } catch(_){ } }, 200);
                } catch(_){}
              }
              // Try any imperative APIs the widget might expose
              try {
                if (typeof el.setAttribute === 'function') el.setAttribute('variant', 'expanded');
                if (typeof el.open === 'function') el.open();
                if (typeof el.show === 'function') el.show();
                if (typeof el.expand === 'function') el.expand();
                if (typeof el.togglePanel === 'function') el.togglePanel(true);
              } catch (_){}

              // Normalize any fixed-position panel to fill the popover
              Array.from(sr.querySelectorAll('*')).forEach(function(n){
                try {
                  var cs = getComputedStyle(n);
                  // Force any fixed/sticky panels to fill the popover
                  if (cs && (cs.position === 'fixed' || cs.position === 'sticky')) {
                    n.style.position = 'absolute';
                    n.style.inset = '0';
                    n.style.width = '100%';
                    n.style.height = '100%';
                    n.style.maxWidth = 'none';
                    n.style.maxHeight = 'none';
                    n.style.margin = '0';
                    n.style.borderRadius = '0';
                    n.style.boxShadow = 'none';
                    n.style.padding = '0';
                  }
                  // Remove transforms that push the card to bottom-right
                  if (cs && cs.transform && cs.transform !== 'none') {
                    n.style.transform = 'none';
                    n.style.translate = 'none';
                    n.style.transformOrigin = 'center center';
                  }
                  // If element is anchored bottom-right, reposition to fill
                  if (cs && (cs.right === '0px' || cs.bottom === '0px')) {
                    n.style.left = '0';
                    n.style.top = '0';
                    n.style.right = 'auto';
                    n.style.bottom = 'auto';
                    n.style.inset = '0';
                    n.style.width = '100%';
                    n.style.height = '100%';
                  }
                } catch (_) {}
              });
            }
          } catch (_) {}

          // Heuristic: find the floating panel/card; stretch or scale it to remove whitespace
          try {
            var container = document.querySelector('.wrap') || document.body || document.documentElement;
            var cw = (container && container.clientWidth) || window.innerWidth;
            var ch = (container && container.clientHeight) || window.innerHeight;

            var candidates = Array.from(document.querySelectorAll('div'));
            var best = null;
            var bestArea = 0;

            candidates.forEach(function (n) {
              try {
                var cs = getComputedStyle(n);
                var r = n.getBoundingClientRect();
                var looksLikePanel =
                  cs &&
                  ((cs.boxShadow && cs.boxShadow !== 'none') || parseFloat(cs.borderRadius || '0') > 8);
                var hasControls = n.querySelector('input, textarea, button, [contenteditable], form');
                var area = (r.width || 0) * (r.height || 0);

                if (looksLikePanel && hasControls && area > bestArea) {
                  best = n;
                  bestArea = area;
                }
              } catch (_) {}
            });

            if (best) {
              // Send preferred size of the actual chat panel to parent to auto-fit the popover
              try {
                var br0 = best.getBoundingClientRect();
                parent.postMessage({ source: 'elevenlabs_iframe', type: 'widget_size', width: Math.round(br0.width), height: Math.round(br0.height) }, '*');
              } catch (_) {}
              // Hard stretch and center fill
              best.style.position = 'absolute';
              best.style.inset = '0';
              best.style.left = '0';
              best.style.top = '0';
              best.style.width = '100%';
              best.style.height = '100%';
              best.style.margin = '0';
              best.style.borderRadius = '0';
              best.style.boxShadow = 'none';
              best.style.maxWidth = 'none';
              best.style.maxHeight = 'none';
              best.style.padding = '0';
              best.style.transform = 'none';

              // Ensure its inner children don't re-introduce margins/padding or transforms
              try {
                Array.from(best.querySelectorAll('*')).forEach(function(n){
                  try {
                    var cs = getComputedStyle(n);
                    n.style.margin = '0';
                    n.style.padding = '0';
                    n.style.borderRadius = '0';
                    if (n.style && n.style.maxWidth) n.style.maxWidth = 'none';
                    if (cs && cs.transform && cs.transform !== 'none') n.style.transform = 'none';
                    if (cs && (cs.position === 'fixed' || cs.position === 'sticky')) {
                      n.style.position = 'absolute';
                      n.style.inset = '0';
                      n.style.width = '100%';
                      n.style.height = '100%';
                    }
                  } catch (_) {}
                });
              } catch (_) {}
            }
          } catch (_) {}

          // Final pass - keep branding visible

          return ok;
        }

        // Run immediately and observe changes in case the widget re-renders late
        var iters = 0;
        var iv = setInterval(function () {
          iters++;
          var done = enhance();
          if (done || iters > 40) clearInterval(iv);
        }, 150);

        var mo = new MutationObserver(function() {
          enhance();
        });
        try { mo.observe(document.body, { childList: true, subtree: true }); } catch (_) {}
        // Observe widget shadow root as well for late branding insertions
        try {
          var __el = document.querySelector('elevenlabs-convai');
          if (__el && __el.shadowRoot) {
            var __mo = new MutationObserver(function() { enhance(); });
            __mo.observe(__el.shadowRoot, { childList: true, subtree: true });
          }
        } catch (_) {}

        // Continuously post the effective widget width/height so the parent can clamp popover size
        try {
          var __targetForSize = null;
          function __findSizeTarget() {
            try {
              var el = document.querySelector('elevenlabs-convai');
              if (!el) return null;
              var sr = el.shadowRoot;
              if (!sr) return el;
              var nodes = Array.from(sr.querySelectorAll('*'));
              var best = null, bestArea = 0;
              nodes.forEach(function(n){
                try {
                  var r = n.getBoundingClientRect();
                  var area = (r.width||0)*(r.height||0);
                  if (area > bestArea) { best = n; bestArea = area; }
                } catch(_){}
              });
              return best || el;
            } catch(_){ return null; }
          }
          function __postSize() {
            try {
              if (!__targetForSize) __targetForSize = __findSizeTarget();
              var r = (__targetForSize || document.body).getBoundingClientRect();
              if (r && r.width && r.height) {
                parent.postMessage({
                  source:'elevenlabs_iframe',
                  type:'widget_size',
                  width: Math.round(r.width),
                  height: Math.round(r.height)
                }, '*');
              }
            } catch(_){}
          }
          // Initial and staggered posts to catch late renders
          __postSize();
          setTimeout(__postSize, 200);
          setTimeout(__postSize, 600);
          // Observe size changes
          try {
            var __ro = new ResizeObserver(function(){ __postSize(); });
            __ro.observe(document.body);
          } catch(_){}
        } catch(_){}

      })();
    </script>
  </body>
</html>`;
  }, [agentId]);

  // Reset local transcript when the popover opens
  useEffect(() => {
    if (open) setTurns([]);
  }, [open]);

  // Close on Escape without fetching transcript (only onConfirm sends)
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        try {
          if (typeof onClose === 'function') onClose();
        } catch (_) {}
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);


  // Aggregate transcript from iframe postMessages
  useEffect(() => {
    if (!open) return;

    const onMsg = (e) => {
      const data = e?.data;
      if (!data || typeof data !== 'object' || data.source !== 'elevenlabs_iframe') return;

      // Handle automatic sizing signal from iframe before processing chat events
      if (data.type === 'widget_size' && data.width && data.height) {
        setAutoSize({ w: data.width, h: data.height });
        return;
      }
      let type = (data.type || '').toLowerCase();
      let payload = data.data || {};
      const extract = (p) => {
        if (!p) return '';
        if (typeof p === 'string') return p;
        // Try common fields emitted by different widget builds
        const direct =
          p.text ||
          p.message ||
          p.transcript ||
          (p.content && (p.content.text || p.content.message)) ||
          p.partial ||
          p.value ||
          '';
        if (direct) return direct;

        // Deep search for first string in common keys {text|message|transcript}
        try {
          const keys = new Set(['text', 'message', 'transcript']);
          const queue = [p];
          const seen = new Set();
          while (queue.length) {
            const cur = queue.shift();
            if (!cur || typeof cur !== 'object') continue;
            if (seen.has(cur)) continue;
            seen.add(cur);
            for (const k of Object.keys(cur)) {
              const v = cur[k];
              if (keys.has(k) && typeof v === 'string' && v.trim()) {
                return v;
              }
              if (v && typeof v === 'object') {
                if (typeof v.text === 'string' && v.text.trim()) return v.text;
                if (typeof v.message === 'string' && v.message.trim()) return v.message;
                if (typeof v.transcript === 'string' && v.transcript.trim()) return v.transcript;
                queue.push(v);
              }
            }
          }
        } catch (_) {}
        return '';
      };
      let text = extract(payload);

      // If this was a generic forwarded postMessage, unwrap inner type/data and re-extract
      if (type === 'postmessage') {
        try {
          const innerType =
            (payload && (payload.type || payload.event)) || '';
          const innerData =
            (payload && (payload.data || payload.detail || payload.payload)) || null;
          if (innerType) type = String(innerType).toLowerCase();
          if (innerData) {
            payload = innerData;
            const innerText = extract(innerData);
            if (innerText) text = innerText;
          }
        } catch (_) {}
      }

      if (!text && !/conversation_ended/.test(type)) return;

      if (/(^|_)user(_|$)|elevenlabs-user-message|user_transcript|elevenlabs-user-transcript/.test(type)) {
        setTurns((prev) => [...prev, { role: 'user', text, ts: Date.now() }]);
      } else if (
        /(^|_)agent(_|$)|elevenlabs-agent-message|elevenlabs-agent-response|agent_response/.test(type)
      ) {
        setTurns((prev) => [...prev, { role: 'agent', text, ts: Date.now() }]);
      } else if (text) {
        // Unknown event type but we have text: alternate role so transcript still builds
        setTurns((prev) => {
          const last = prev[prev.length - 1];
          const role = last?.role === 'user' ? 'agent' : 'user';
          return [...prev, { role, text, ts: Date.now() }];
        });
      } else if (/conversation_ended/.test(type)) {
        // No-op: explicit "I'm Done" controls completion UX
      }
    };

    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [open]);

  const transcriptText = useMemo(
    () => turns.map((t) => `${t.role === 'agent' ? 'Agent' : 'User'}: ${t.text}`).join('\n'),
    [turns]
  );

  // Fallback: scrape transcript from the iframe DOM if events didn't fire
  const collectTranscriptFromIframe = useCallback(() => {
    try {
      const doc = iframeRef.current?.contentWindow?.document;
      if (!doc) return null;

      let container;
      try {
        const el = doc.querySelector('elevenlabs-convai');
        const sr = el && el.shadowRoot;
        // Prefer the widget's shadow root, since visible chat text usually lives there
        container = sr || (doc.querySelector('.wrap') || doc.body || doc.documentElement);
      } catch (_) {
        container = doc.querySelector('.wrap') || doc.body || doc.documentElement;
      }

      // Try to find the main chat panel area by heuristics
      let best = null;
      let bestArea = 0;
      Array.from(container.querySelectorAll('div')).forEach((n) => {
        try {
          const cs = doc.defaultView.getComputedStyle(n);
          const r = n.getBoundingClientRect();
          const looksLikePanel =
            cs &&
            ((cs.boxShadow && cs.boxShadow !== 'none') ||
              parseFloat(cs.borderRadius || '0') > 8);
          const hasControls =
            n.querySelector &&
            n.querySelector('input, textarea, button, [contenteditable], form');
          const area = (r.width || 0) * (r.height || 0);
          if (looksLikePanel && hasControls && area > bestArea) {
            best = n;
            bestArea = area;
          }
        } catch (_) {}
      });

      const scope = best || container;

      // Collect visible text nodes within the scope
      const walker = doc.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
      const rawLines = [];
      let node;
      // Basic noise filters
      const isNoise = (s) => {
        const t = (s || '').trim().toLowerCase();
        if (!t) return true;
        if (t === 'send' || t === 'start' || t === 'stop' || t === 'open' || t === 'close' || t === 'settings') return true;
        if (t.includes('powered by') || t.includes('elevenlabs')) return true;
        return false;
      };
      while ((node = walker.nextNode())) {
        const text = (node.nodeValue || '').replace(/\s+/g, ' ').trim();
        if (!text || isNoise(text)) continue;
        rawLines.push(text);
      }

      // Deduplicate consecutive duplicates and short fragments
      const cleaned = [];
      for (let i = 0; i < rawLines.length; i++) {
        const cur = rawLines[i];
        if (cur.length < 2) continue;
        if (i === 0 || cur !== rawLines[i - 1]) cleaned.push(cur);
      }

      if (!cleaned.length) return null;

      const transcript = cleaned.join('\n');
      const turns = cleaned.map((text, idx) => ({
        role: idx % 2 === 0 ? 'user' : 'agent',
        text,
        ts: Date.now() + idx
      }));

      return { transcript, turns };
    } catch (_) {
      return null;
    }
  }, []);

  const handleConfirm = () => {
    if (typeof onConfirm !== 'function') return;
    const hasStructured = Array.isArray(turns) && turns.length > 0;
    const hasText = typeof transcriptText === 'string' && transcriptText.trim().length > 0;

    if (hasStructured || hasText) {
      onConfirm(transcriptText, turns);
      return;
    }

    // Fallback: scrape directly from iframe DOM
    const fallback = collectTranscriptFromIframe();
    if (fallback && ((fallback.transcript && fallback.transcript.trim()) || (fallback.turns && fallback.turns.length))) {
      onConfirm(fallback.transcript || '', fallback.turns || []);
    } else {
      onConfirm('', []);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        backgroundColor: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      {/* Centered popover container (transparent, widget-only surface) */}
      <Box
        sx={{
          // Clamp popover width close to measured widget width to avoid huge side gutters
          width: autoSize
            ? (Math.max(480, Math.min(
                autoSize.w,
                (typeof window !== 'undefined' ? window.innerWidth * 0.92 : autoSize.w)
              )) + 'px')
            : 'min(520px, 92vw)',
          height: autoSize
            ? (Math.max(560, Math.min(
                autoSize.h + 96,
                (typeof window !== 'undefined' ? window.innerHeight * 0.92 : autoSize.h + 96)
              )) + 'px')
            : 'min(820px, 92vh)',
          bgcolor: '#FFFFFF',
          borderRadius: '16px',
          boxShadow: '0 18px 56px rgba(0,0,0,0.36)',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #EAECF0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <DragIndicatorIcon sx={{ color: '#98A2B3' }} fontSize="small" />
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: '#101828',
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
              }}
            >
              Conversational Discovery
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={() => { try { if (typeof onClose === 'function') onClose(); } catch (_) {} }}
            size="small"
            sx={{ color: '#667085' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Full-bleed widget area */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <iframe
            ref={iframeRef}
            title="ElevenLabs Intake"
            srcDoc={iframeSrcDoc}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
            allow="microphone; autoplay; clipboard-write; camera; fullscreen"
            style={{
              border: 0,
              width: '100%',
              height: '100%',
              display: 'block',
              background: 'transparent',
              borderRadius: '0px',
              boxShadow: 'none'
            }}
          />
        </Box>

        {/* Footer actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            p: 2,
            pt: 1,
            borderTop: '1px solid #EAECF0'
          }}
        >
          <Button
            onClick={() => { try { if (typeof onClose === 'function') onClose(); } catch (_) {} }}
            size="medium"
            variant="text"
            sx={{
              color: '#101828',
              borderRadius: '9999px',
              px: 1.5,
              py: 1,
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(16,24,40,0.04)' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            size="medium"
            variant="contained"
            sx={{
              bgcolor: '#4599FA',
              borderRadius: '9999px',
              px: 2.5,
              py: 1,
              boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
              textTransform: 'none',
              '&:hover': { bgcolor: '#3b82d6' }
            }}
          >
            I’m done
          </Button>
        </Box>

      </Box>
    </Box>
  );
}
